import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Card from "components/card";
import { MdArrowBack, MdSave, MdError } from "react-icons/md";
import axios from "axios";
import toast from "react-hot-toast";

const EditUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState({
    full_name: "",
    username: "",
    email: "",
    phone: "",
    role: "",
    unit: ""
  });

  useEffect(() => {
    fetchUserData();
  }, [id]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`https://usibras-web-backend.onrender.com/api/v1/users/${id}`);
      if (response.data.user) {
        setUserData(response.data.user);
      }
    } catch (err) {
      setError('Failed to fetch user data');
      console.error('Error fetching user:', err);
      toast.error('Failed to fetch user data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await axios.put(`https://usibras-web-backend.onrender.com/api/v1/users/${id}`, userData);
      if (response.data.success) {
        toast.success('User updated successfully');
        navigate('/admin/users');
      } else {
        toast.error(response.data.message || 'Failed to update user');
      }
    } catch (err) {
      console.error('Error updating user:', err);
      toast.error(err.response?.data?.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <div className="text-center">
          <div className="h-24 w-24 animate-spin rounded-full border-t-4 border-b-4 border-brand-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-white">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <div className="text-center">
          <MdError className="h-16 w-16 text-red-500 mx-auto" />
          <p className="mt-4 text-gray-600 dark:text-white">{error}</p>
          <button
            onClick={() => navigate('/admin/users')}
            className="mt-4 bg-brand-500 text-white px-4 py-2 rounded-lg hover:bg-brand-600"
          >
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 h-full">
      <Card extra={"w-full h-full p-4"}>
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/admin/users')}
              className="mr-4 flex items-center text-gray-600 hover:text-gray-800 dark:text-white"
            >
              <MdArrowBack className="h-6 w-6" />
            </button>
            <h4 className="text-xl font-bold text-navy-700 dark:text-white">
              Edit User
            </h4>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label className="text-sm text-navy-700 dark:text-white">Full Name</label>
              <input
                type="text"
                name="full_name"
                value={userData.full_name}
                onChange={handleChange}
                className="mt-2 w-full rounded-lg border border-gray-200 p-2.5 text-sm text-navy-700 outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                required
              />
            </div>

            <div>
              <label className="text-sm text-navy-700 dark:text-white">Username</label>
              <input
                type="text"
                name="username"
                value={userData.username}
                onChange={handleChange}
                className="mt-2 w-full rounded-lg border border-gray-200 p-2.5 text-sm text-navy-700 outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                required
              />
            </div>

            <div>
              <label className="text-sm text-navy-700 dark:text-white">Email</label>
              <input
                type="email"
                name="email"
                value={userData.email}
                onChange={handleChange}
                className="mt-2 w-full rounded-lg border border-gray-200 p-2.5 text-sm text-navy-700 outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                required
              />
            </div>

            <div>
              <label className="text-sm text-navy-700 dark:text-white">Phone</label>
              <input
                type="tel"
                name="phone"
                value={userData.phone}
                onChange={handleChange}
                className="mt-2 w-full rounded-lg border border-gray-200 p-2.5 text-sm text-navy-700 outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
              />
            </div>

            <div>
              <label className="text-sm text-navy-700 dark:text-white">Role</label>
              <select
                name="role"
                value={userData.role}
                onChange={handleChange}
                className="mt-2 w-full rounded-lg border border-gray-200 p-2.5 text-sm text-navy-700 outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                required
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {/* Units dropdown - only show when role is "user" */}
            {userData.role === "user" && (
              <div>
                <label className="text-sm text-navy-700 dark:text-white">
                  Unit*
                </label>
                <select
                  name="unit"
                  value={userData.unit || ""}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-lg border border-gray-200 p-2.5 text-sm text-navy-700 outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                  required
                >
                  <option value="">Select a unit</option>
                  <option value="RCN Warehouse">RCN Warehouse</option>
                  <option value="Finish Pro Warehouse">Finish Pro Warehouse</option>
                  <option value="Analyst">Analyst</option>
                  <option value="Manual Peeling">Manual Peeling</option>
                  <option value="Field Analyst">Field Analyst</option>
                  <option value="Factory Analyst">Factory Analyst</option>
                </select>
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/admin/users')}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-navy-600 dark:text-white dark:hover:bg-navy-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
            >
              <MdSave className="h-5 w-5" />
              Save Changes
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default EditUser; 